import AIJob, { AIJobType } from 'models/AIJob';
import Review from 'models/Review';
import Admission, { AdmissionOptions } from 'models/reviews/Admission';
import Authorization, { AuthorizationOptions } from 'models/reviews/Authorization';
import Referral, { ReferralOptions } from 'models/reviews/Referral';
import ReviewModelFactory from 'models/reviews/ReviewModelFactory';
import Summarization, { SummarizationOptions } from 'models/Summarization';
import { admissionQueryKeys } from 'services/api/admissions';
import { authorizationQueryKeys, referralQueryKeys } from 'services/api/reviews';
import { summarizationQueryKeys } from 'services/api/summarizations';
import { queryClient } from 'services/http';
import {
  type AIJobNotificationContext,
  AIJobNotificationType,
  useAIJobNotificationStore,
} from 'stores/aiJobNotificationStore';
import { useNotificationStore } from 'stores/notificationStore';

import { simpleDateAtTime } from './date';

export const notificationQueryKeyMap = {
  [AIJobNotificationType.ADMISSION_SUMMARY]: admissionQueryKeys,
  [AIJobNotificationType.AUTHORIZATION_SUMMARY]: authorizationQueryKeys,
  [AIJobNotificationType.AUTHORIZATION_COMPARISON]: authorizationQueryKeys,
  [AIJobNotificationType.REFERRAL_SUMMARY]: referralQueryKeys,
  [AIJobNotificationType.WORKSPACE_SUMMARY]: summarizationQueryKeys,
};

/**
 * Generates the notification title based on job context and state
 */
export const getAIJobNotificationTitle = (
  context: AIJobNotificationContext,
  state: 'processing' | 'completed' | 'failed'
): string => {
  const description = getJobDescription(context);

  switch (state) {
    case 'processing':
      return `Currently processing ${description}`;
    case 'completed':
      return `Processing complete for ${description}`;
    case 'failed':
      return `Processing failed for ${description}`;
  }
};

/**
 * Gets the job description for the notification title
 */
const getJobDescription = (context: AIJobNotificationContext): string => {
  const { jobType, summaryType, patientName, createdAt } = context;

  switch (jobType) {
    case AIJobNotificationType.ADMISSION_SUMMARY:
      return `Admissions - ${simpleDateAtTime(createdAt)}`;

    case AIJobNotificationType.AUTHORIZATION_SUMMARY:
      return `${patientName} - Concurrent Review Summary`;

    case AIJobNotificationType.AUTHORIZATION_COMPARISON:
      return `${patientName} - Concurrent Review Comparison`;

    case AIJobNotificationType.REFERRAL_SUMMARY:
      return `${patientName} - Referral Summary`;

    case AIJobNotificationType.WORKSPACE_SUMMARY:
      return `${summaryType} - ${simpleDateAtTime(createdAt)}`;

    default:
      return `${jobType}`;
  }
};

/**
 * Checks if the current path matches the return URL for a job
 * Used to determine if we should hide the notification (user is on the loading page)
 */
export const isOnJobLoadingPage = (currentPath: string, context: AIJobNotificationContext): boolean => {
  // Normalize paths by removing trailing slashes
  const normalizedCurrentPath = currentPath.replace(/\/$/, '');
  const normalizedReturnUrl = context.returnUrl.replace(/\/$/, '');

  return normalizedCurrentPath === normalizedReturnUrl;
};

export type SummarizableReview = Authorization | Referral | Admission;
export type Jobbable = SummarizableReview | Summarization;

export const updateJobbableForAIJob = (job: AIJob, jobbableId: string, summaryType: AIJobNotificationType) => {
  if (job.isPending || job.isRunning) return;

  const queryKey = notificationQueryKeyMap[summaryType].show({ id: jobbableId });

  if (job.isAwaiting) {
    const jobbable = queryClient.getQueryData(queryKey) as Jobbable | undefined;

    // only update cache if job is transitioning from pending to awaiting
    if (jobbable?.summaryJob?.isPending) {
      updateJobbableCacheWithAIJob(queryKey, job);
    }

    return;
  }

  // We can't simply invalidate here because of this edge case:
  //   - User kicks off a summary for Patient A
  //   - User navigates to Patient B and kicks off another summary
  //   - User stays on Patient B's summary tab (loading state)
  //   - Notification for Patient A's completed summary appears, user clicks links
  //     - At this point, Patient A's review query is only marked as stale
  //   - User is taken to Patient A's summary tab, which *briefly displays the empty state*
  // The brief empty state is because the review refetched when it's needed, not before,
  // and the stale, cached review does not have a summary artifact yet
  queryClient.refetchQueries({ queryKey });

  // The refetch above targets the show query to avoid the empty state flash described
  // above. We also need to invalidate the base key so list views (e.g. admissions table)
  // pick up the completed job state — the show query may be inactive if the user navigated
  // away (e.g. closed the slideout), and list queries aren't covered by the show refetch.
  const baseKey = notificationQueryKeyMap[summaryType].base;
  queryClient.invalidateQueries({ queryKey: baseKey });
};

export const jobPropertyForType = (type: AIJobType) => {
  switch (type) {
    case AIJobType.PATIENT_DEMOGRAPHICS:
      return 'patientDemographicsJob';
    case AIJobType.COMPARISON:
      return 'comparisonJobForCurrentSummary';
    case AIJobType.CRITERIA_EVALUATION:
      return 'criteriaEvaluationJob';
    case AIJobType.SUMMARY:
    default:
      return 'summaryJobForCurrentAttachments';
  }
};

type SummarizableReviewOptions = AuthorizationOptions | ReferralOptions | AdmissionOptions;

export const updateJobbableCacheWithAIJob = (queryKey: any[], job: AIJob) => {
  const jobProperty = jobPropertyForType(job.type!);

  queryClient.setQueryData(queryKey, (jobbable: Jobbable) => {
    const payload = { ...jobbable, [jobProperty]: job };

    return jobbable instanceof Review
      ? ReviewModelFactory.get(payload as SummarizableReviewOptions)
      : new Summarization(payload as SummarizationOptions);
  });
};

/**
 * Updates the notification title for a tracked job by jobbableId.
 * Coordinates between aiJobNotificationStore and notificationStore.
 */
export const updateJobNotificationTitle = (jobbableId: string, newTitle: string) => {
  const { trackedJobs, jobNotificationMap, updateTrackedAIJob } = useAIJobNotificationStore.getState();
  const { notifications, actions } = useNotificationStore.getState();

  const entry = Array.from(trackedJobs.entries()).find(([, context]) => context.jobbableId === jobbableId);
  if (!entry) return;

  const [trackingKey, jobContext] = entry;
  const notificationId = jobNotificationMap.get(trackingKey);
  if (!notificationId) return;

  const notification = notifications.find((n) => n.id === notificationId);
  if (!notification) return;

  updateTrackedAIJob(trackingKey, { summaryType: newTitle });
  actions.updateNotification(notificationId, {
    title: getAIJobNotificationTitle({ ...jobContext, summaryType: newTitle }, notification.state),
  });
};
